// ServiceFlavorsWorkspace.js — top-level Service Flavors builder.
// Derives takeoff service lines via the real engine chain, seeds 3 default
// flavors (Basic / Standard / Premium), prices each through computeFlavor, and
// renders a global controls band + 3 FlavorCards + a per-flavor DeeperTweaksModal.
// All pricing math lives in serviceFlavorsModel / estimateEngine; this file owns
// React state, the derivation memo chain, mutation handlers, and view-models.
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { calculateLaborHours } from '../../../SiteTakeoff/laborCalculator';
import {
  DEFAULT_ESTIMATE_SETTINGS,
  buildServiceLinesFromTakeoff,
  computeService,
  heatFactorForMonth,
} from '../estimateEngine';
import { applyRateOverrides } from '../applyRateOverrides';
import {
  computeFlavor,
  seedFromTakeoff,
  roundTo,
  clamp,
  FREQS,
} from './serviceFlavorsModel';
import { buildDefaultFlavors, STARTUP_CATALOG } from './defaultFlavors';
import ScopePanel from '../scope/ScopePanel';
import SettingsModal, { RatesStrip } from '../scope/SettingsModal';
import FlavorCard from './FlavorCard';
import DeeperTweaksModal from './DeeperTweaksModal';
import './serviceFlavors.css';

// ── helpers ────────────────────────────────────────────────────────────────

// rateSeed.rates is keyed by CATEGORY (mtc/arborist/herbicide/irrigation); the
// engine reads settings.laborRates by ROLE (Maint/Arborist/Herbicide/Irrigation).
// Translate explicitly — a naive {...d, ...rates} would never reach the engine.
const seededLaborRates = (rateSeed) => {
  const r = (rateSeed && rateSeed.rates) || {};
  const d = DEFAULT_ESTIMATE_SETTINGS.laborRates;
  return {
    ...d,
    Maint: Number(r.mtc) || d.Maint,
    Arborist: Number(r.arborist) || d.Arborist,
    Herbicide: Number(r.herbicide) || d.Herbicide,
    Irrigation: Number(r.irrigation) || d.Irrigation,
  };
};

// Map the engine's modifierTable (string keys) to laborCalculator's modifier
// keys. Mirrors TakeoffEstimateWorkspace so the same real multipliers apply.
const toLaborCalculatorModifiers = (modifierTable = {}) => ({
  access_obstructed: Number(modifierTable.obstructed) || 1,
  slope_sloped: Number(modifierTable.sloped) || 1,
  litterExposure_high: Number(modifierTable['high-litter']) || 1,
  weedPressure_med: Number(modifierTable['med-weed']) || 1,
  weedPressure_high: Number(modifierTable['high-weed']) || 1,
  limitedAccess: Number(modifierTable['limited-access']) || 1,
  blowRestrictions: Number(modifierTable['blow-restrictions']) || 1,
});

const money = (n) => '$' + Math.round(Number(n) || 0).toLocaleString('en-US');
const fmtNumber = (n, d = 1) => (Number(n) || 0).toLocaleString('en-US', {
  minimumFractionDigits: d, maximumFractionDigits: d,
});

// Per-service display metadata. accent feeds the colored dot on each row.
const SERVICE_META = [
  { role: 'Maint', label: 'Maintenance', delivery: 'labor', accent: '#3a5a8c' },
  { role: 'Irrigation', label: 'Irrigation', delivery: 'labor', accent: '#2f7350' },
  { role: 'Herbicide', label: 'Herbicide', delivery: 'flat', accent: '#6b4e9e' },
  { role: 'Arborist', label: 'Arborist', delivery: 'tree', accent: '#8a4a36' },
  { role: 'Fert', label: 'Fertilization', delivery: 'flat', accent: '#9a7d2c' },
];

const fiClamp = (fi) => clamp(typeof fi === 'number' ? fi : 4, 0, FREQS.length - 1);

// ── component ──────────────────────────────────────────────────────────────

export default function ServiceFlavorsWorkspace({
  jobsite,
  areas = [],
  takeoff,
  rateSeed,
  onBackToTakeoff,
  onToast, // eslint-disable-line no-unused-vars -- reserved for Phase-2 save
}) {
  // Engine settings: seasonal heat for the current month + seeded labor rates.
  // STATE (not a memo) so the Settings modal can edit production rates / labor /
  // modifiers / subcontractors / frequencies — edits re-derive hours → re-price.
  const [settings, setSettings] = useState(() => ({
    ...DEFAULT_ESTIMATE_SETTINGS,
    laborRates: seededLaborRates(rateSeed),
    seasonalHeatFactor: heatFactorForMonth(new Date().getMonth()),
  }));
  // Re-apply seeded customer labor rates when the rate-seed changes (preserves
  // any other in-modal edits — only laborRates is refreshed from the seed).
  useEffect(() => {
    setSettings((cur) => ({ ...cur, laborRates: seededLaborRates(rateSeed) }));
  }, [rateSeed]);
  const onSettingsChange = (updater) => setSettings(updater);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Real derivation chain: areas → labor hours → engine service lines → seed.
  const hoursResult = useMemo(() => calculateLaborHours(areas, {
    ...settings.productionRates,
    _modifiers: toLaborCalculatorModifiers(settings.modifierTable),
  }), [areas, settings]);

  const baseServices = useMemo(
    () => buildServiceLinesFromTakeoff(areas, hoursResult, settings),
    [areas, hoursResult, settings],
  );

  // ── scope state (shared, all flavors inherit) ──
  // enabledByRole overrides a service's enabled flag; rateOverrides edits per-task
  // coverage/frequency. The adjusted `services` feed BOTH the ScopePanel and the
  // flavor pricing, so editing scope re-prices all three flavors live.
  const [enabledByRole, setEnabledByRole] = useState({});
  const [rateOverrides, setRateOverrides] = useState({});

  const services = useMemo(
    () => applyRateOverrides(
      baseServices.map((s) => ({ ...s, enabled: enabledByRole[s.role] ?? s.enabled })),
      rateOverrides,
      settings,
    ),
    [baseServices, enabledByRole, rateOverrides, settings],
  );

  const seed = useMemo(() => seedFromTakeoff(services, areas), [services, areas]);

  // Total annual labor hours across labor services (for the scope header).
  const totalLaborHours = useMemo(
    () => services
      .filter((s) => s.delivery === 'labor' && s.enabled)
      .reduce((sum, s) => sum + computeService(s, settings).hours, 0),
    [services, settings],
  );

  // ── state ──
  const [globals, setGlobals] = useState({ margin: 50, driveRate: 45, taxable: false, taxRate: 8.05 });
  const [flavors, setFlavors] = useState(() => buildDefaultFlavors(seed));
  const [modalTier, setModalTier] = useState(null);
  const [flavorsCollapsed, setFlavorsCollapsed] = useState(false);

  // Re-seed once when a non-empty takeoff seed first arrives. The initial mount
  // may have empty areas (all annualHours/counts = 0); we don't want to lock
  // zeros in, but once the user starts tweaking we must not clobber their edits.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    const hasData = Object.values(seed).some(
      (s) => Number(s?.annualHours) > 0 || Number(s?.trees) > 0 || Number(s?.palms) > 0,
    );
    if (!hasData) return;
    setFlavors(buildDefaultFlavors(seed));
    seededRef.current = true;
  }, [seed]);

  // Total mapped square footage (parent areas only).
  const totalSF = useMemo(
    () => areas
      .filter((a) => !a.parentAreaId)
      .reduce((s, a) => s + (Number(a.sqft ?? a.area) || 0), 0),
    [areas],
  );
  const areaCount = areas.filter((a) => !a.parentAreaId).length;

  // ── scope mutations (shared base scope) ──
  // Per-task coverage/frequency edit → rateOverrides[serviceCode]; recomputed by
  // applyRateOverrides into `services`, which re-prices every flavor.
  const handleRateChange = (serviceCode, patch) => {
    if (!serviceCode) return;
    setRateOverrides((cur) => ({ ...cur, [serviceCode]: { ...cur[serviceCode], ...patch } }));
  };
  const toggleRole = (role) => setEnabledByRole((cur) => {
    const current = cur[role] ?? (baseServices.find((s) => s.role === role)?.enabled ?? false);
    return { ...cur, [role]: !current };
  });

  // ── mutations (pure setState updaters) ──
  const patchSvc = (fid, role, fn) => setFlavors((fs) => fs.map(
    (f) => (f.id === fid ? { ...f, svc: { ...f.svc, [role]: fn(f.svc[role]) } } : f),
  ));
  const patchFlavor = (fid, fn) => setFlavors((fs) => fs.map((f) => (f.id === fid ? fn(f) : f)));

  const cycleMode = (fid, role) => {
    const next = { in: 'extra', extra: 'off', off: 'in' };
    patchSvc(fid, role, (st) => ({ ...st, mode: next[st.mode] }));
  };
  const stepFreq = (fid, role, d) => patchSvc(fid, role, (st) => ({
    ...st, fi: clamp((st.fi ?? 4) + d, 0, FREQS.length - 1),
  }));
  // Arborist dial edits the tree count; other count/flat roles are frequency-driven.
  // Labor roles (Maint/Irrigation) use a per-flavor hours % adjuster (50–150).
  const stepDur = (fid, role, d) => patchSvc(fid, role, (st) => {
    if (role === 'Arborist') return { ...st, trees: Math.max(0, (st.trees || 0) + d) };
    return { ...st, hoursPct: clamp((st.hoursPct ?? 100) + d * 5, 50, 150) };
  });

  const setMargin = (v) => setGlobals((g) => ({ ...g, margin: clamp(Number(v) || 0, 20, 95) }));
  const toggleTax = () => setGlobals((g) => ({ ...g, taxable: !g.taxable }));
  const stepTax = (d) => setGlobals((g) => ({ ...g, taxRate: clamp(Math.round((g.taxRate + d) * 100) / 100, 0, 15) }));

  // modal mutations
  const setDays = (fid, n) => patchSvc(fid, 'Maint', (st) => ({ ...st, days: n }));
  const stepBlow = (fid, d) => patchSvc(fid, 'Maint', (st) => ({ ...st, blowGo: clamp((st.blowGo || 0) + d, 0, 104) }));
  const setPrune = (fid, v) => patchSvc(fid, 'Maint', (st) => ({ ...st, prune: v }));
  const setCov = (fid, i) => patchSvc(fid, 'Irrigation', (st) => ({ ...st, cov: i }));
  const toggleStartup = (fid, key) => patchFlavor(fid, (f) => ({
    ...f, startup: { ...f.startup, [key]: { ...f.startup[key], on: !f.startup[key].on } },
  }));
  const stepStartup = (fid, key, d) => patchFlavor(fid, (f) => ({
    ...f, startup: { ...f.startup, [key]: { ...f.startup[key], cost: clamp((f.startup[key].cost || 0) + d * 100, 0, 50000) } },
  }));

  // ── pricing + view-models ──
  const { vms, chips, pricedById } = useMemo(() => {
    const priced = flavors.map((f) => ({ f, c: computeFlavor(f, globals, settings, services) }));
    const std = priced.find((p) => p.f.id === 'standard') || priced[1] || priced[0];
    const stdAnnualR = std ? roundTo(std.c.monthly, 5) * 12 : 0;

    const builtVms = priced.map(({ f, c }) => {
      const monthlyR = roundTo(c.monthly, 5);
      const annualR = monthlyR * 12;
      const isBase = f.id === 'standard';
      const d = annualR - stdAnnualR;
      const deltaStr = isBase ? 'Baseline' : (d >= 0 ? '+' : '−') + money(Math.abs(d));
      const deltaDir = isBase ? 'base' : (d >= 0 ? 'up' : 'down');

      // extras by role → quick lookup for service feeStr + add-ons list
      const extraByRole = {};
      (c.extras || []).forEach((e) => { extraByRole[e.role] = e; });

      const services = SERVICE_META.map((meta) => {
        const st = f.svc[meta.role] || { mode: 'off' };
        const role = meta.role;
        const isMaint = role === 'Maint';
        const isTree = meta.delivery === 'tree';
        const isFlat = meta.delivery === 'flat';
        const isLaborRole = role === 'Maint' || role === 'Irrigation';

        // total string by mode — labor roles show REAL hrs/yr from the engine.
        let totalStr = '—';
        if (st.mode === 'extra') {
          const ex = extraByRole[role];
          totalStr = ex ? `+${money(roundTo(ex.feeMonthly, 5))}/mo` : '—';
        } else if (st.mode === 'in') {
          if (isLaborRole) {
            totalStr = `${fmtNumber(c.hoursByRole?.[role] || 0, 1)} hrs/yr`;
          } else if (isTree) {
            totalStr = `${st.trees || 0} trees · ${st.palms || 0} palms`;
          } else if (isFlat) {
            totalStr = `${FREQS[fiClamp(st.fi)].v} visits`;
          }
        }

        // freq + duration labels (ServiceRow falls back to its own if omitted)
        const freqLabel = isMaint
          ? `${st.days}×/wk`
          : (isFlat || isTree)
            ? `${FREQS[fiClamp(st.fi)].v} visits`
            : FREQS[fiClamp(st.fi)].f;
        const durLabel = isTree
          ? `${st.trees || 0} trees · ${st.palms || 0} palms`
          : isFlat
            ? ''
            : `${st.hoursPct ?? 100}%`;
        const durCaption = isTree ? 'Trees · palms' : isFlat ? 'Per visit' : 'Hours %';

        const feeStr = st.mode === 'extra' && extraByRole[role]
          ? `${money(roundTo(extraByRole[role].feeMonthly, 5))}/mo`
          : undefined;

        return { meta, st, totalStr, freqLabel, durLabel, durCaption, feeStr };
      });

      const extras = (c.extras || []).map((e) => {
        const m = SERVICE_META.find((mm) => mm.role === e.role);
        return {
          label: m ? m.label : e.role,
          accent: m ? m.accent : undefined,
          feeStr: '+' + money(roundTo(e.feeMonthly, 5)) + '/mo',
        };
      });

      return {
        id: f.id,
        name: f.name,
        recommended: f.recommended,
        priceStr: money(annualR),
        monthlyStr: money(monthlyR) + ' /mo',
        costStr: money(c.cost),
        sfStr: '$' + (totalSF > 0 ? (annualR / totalSF).toFixed(2) : '0.00'),
        taxable: globals.taxable,
        taxRateStr: globals.taxRate.toFixed(2) + '%',
        withTaxStr: money(monthlyR * (1 + globals.taxRate / 100)) + ' /mo',
        hasStartup: c.startupPrice > 0,
        startupStr: money(c.startupPrice),
        deltaStr,
        deltaDir,
        services,
        extras,
        hasExtras: extras.length > 0,
      };
    });

    const chipData = builtVms.map((vm) => ({
      id: vm.id,
      name: vm.name,
      priceStr: vm.priceStr,
      deltaStr: vm.deltaStr,
      recommended: vm.recommended,
    }));

    const byId = {};
    priced.forEach(({ f, c }) => { byId[f.id] = c; });

    return { vms: builtVms, chips: chipData, pricedById: byId };
  }, [flavors, globals, settings, services, totalSF]);

  // active modal flavor + its priced result
  const modalFlavor = modalTier ? flavors.find((f) => f.id === modalTier) : null;
  const modalPriced = modalTier ? pricedById[modalTier] : null;

  const lowMargin = globals.margin < 45;

  return (
    <div className="service-flavors" style={{ padding: 24 }}>
      {/* Title row */}
      <div
        className="sf-title-row"
        style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}
      >
        <div style={{ minWidth: 0 }}>
          {onBackToTakeoff ? (
            <button
              type="button"
              className="sf-back"
              onClick={onBackToTakeoff}
              style={{
                border: 'none', background: 'transparent', cursor: 'pointer',
                color: 'var(--sf-muted)', fontSize: 12.5, padding: 0, marginBottom: 6,
              }}
            >
              ← Back to takeoff
            </button>
          ) : null}
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: 'var(--sf-ink)' }}>
            Build estimate — service flavors
          </h1>
          <div style={{ fontSize: 13, color: 'var(--sf-muted)', marginTop: 4 }}>
            Three ready-to-pitch tiers. Tune the dials, watch the price move.
          </div>
        </div>
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--sf-ink-2)' }}>
            {jobsite?.name || takeoff?.name || 'Untitled site'}
          </div>
          <div className="num" style={{ fontSize: 12, color: 'var(--sf-muted)' }}>
            {totalSF.toLocaleString()} SF · {areaCount} areas
          </div>
        </div>
      </div>

      {/* Toolbar: collapse toggle + Settings */}
      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
        <button
          type="button"
          className="sf-settings-btn"
          onClick={() => setSettingsOpen(true)}
          style={{
            border: '1px solid var(--sf-line-2)', background: 'var(--sf-surface)',
            borderRadius: 8, cursor: 'pointer', padding: '5px 12px',
            fontSize: 12, fontWeight: 700, color: 'var(--sf-ink-2)',
          }}
        >
          ⚙ Settings
        </button>
        <button
          type="button"
          className="sf-collapse-toggle"
          onClick={() => setFlavorsCollapsed((c) => !c)}
          style={{
            border: '1px solid var(--sf-line-2)', background: 'var(--sf-surface)',
            borderRadius: 8, cursor: 'pointer', padding: '5px 12px',
            fontSize: 12, fontWeight: 700, color: 'var(--sf-ink-2)',
          }}
        >
          {flavorsCollapsed ? 'Show flavors ▸' : 'Hide flavors ◂'}
        </button>
      </div>

      {/* Two-column layout: scope (left) + flavors (right) */}
      <div className={`sf-estimate-layout${flavorsCollapsed ? ' is-collapsed' : ''}`}>
        {/* LEFT — takeoff scope & frequency (shared base scope) */}
        <div className="sf-scope-col">
          <ScopePanel
            services={services}
            settings={settings}
            onToggleRole={toggleRole}
            onRateChange={handleRateChange}
            totalLaborHours={totalLaborHours}
          />
        </div>

        {/* RIGHT — flavors */}
        <div className={`sf-flavors-col${flavorsCollapsed ? ' is-collapsed' : ''}`}>

      {/* Global controls band */}
      <div
        className="sf-band"
        style={{
          display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
          padding: '14px 18px', marginBottom: 18,
        }}
      >
        {/* Margin slider */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 220 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, letterSpacing: '.05em', textTransform: 'uppercase',
              color: 'var(--sf-muted)', fontWeight: 700 }}>
              Target margin
            </span>
            <span className="num" style={{ fontSize: 14, fontWeight: 700, color: 'var(--sf-ink-2)' }}>
              {globals.margin}%
            </span>
            {lowMargin ? (
              <span
                className="sf-band-warn"
                style={{
                  fontSize: 10.5, fontWeight: 700, color: '#fff', background: 'var(--sf-warn)',
                  borderRadius: 999, padding: '2px 8px',
                }}
              >
                Below 45%
              </span>
            ) : null}
          </div>
          <input
            type="range"
            min="20"
            max="95"
            step="1"
            value={globals.margin}
            onChange={(e) => setMargin(e.target.value)}
            aria-label="Target margin"
            style={{ width: '100%' }}
          />
        </div>

        {/* Drive-time stepper hidden this pass (drive-time deferred). */}

        {/* Taxable toggle + rate stepper */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 11, letterSpacing: '.05em', textTransform: 'uppercase',
            color: 'var(--sf-muted)', fontWeight: 700 }}>
            Sales tax
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              onClick={toggleTax}
              className="sf-band-toggle"
              aria-pressed={globals.taxable}
              style={{
                border: '1px solid var(--sf-line-2)', borderRadius: 999, cursor: 'pointer',
                padding: '3px 12px', fontSize: 11.5, fontWeight: 700,
                color: globals.taxable ? '#fff' : 'var(--sf-muted)',
                background: globals.taxable ? 'var(--sf-green)' : 'var(--sf-surface)',
              }}
            >
              {globals.taxable ? 'Taxable' : 'No tax'}
            </button>
            <button type="button" aria-label="decrease tax rate" onClick={() => stepTax(-0.25)} className="sf-band-step">−</button>
            <span className="num" style={{ minWidth: 48, textAlign: 'center', fontWeight: 600, color: 'var(--sf-ink-2)' }}>
              {globals.taxRate.toFixed(2)}%
            </span>
            <button type="button" aria-label="increase tax rate" onClick={() => stepTax(0.25)} className="sf-band-step">+</button>
          </div>
        </div>

        {/* Price chips */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
          {chips.map((chip) => (
            <div
              key={chip.id}
              className="sf-band-chip"
              style={{
                display: 'flex', flexDirection: 'column', gap: 2, padding: '8px 12px',
                borderRadius: 10, border: '1px solid var(--sf-line)', background: 'var(--sf-inset)',
                minWidth: 96,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {chip.recommended ? (
                  <span
                    className="sf-band-chip-dot"
                    style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--sf-green)' }}
                  />
                ) : null}
                <span style={{ fontSize: 10.5, letterSpacing: '.05em', textTransform: 'uppercase',
                  color: 'var(--sf-muted)', fontWeight: 700 }}>
                  {`${chip.name} tier`}
                </span>
              </div>
              <span className="num" style={{ fontSize: 15, fontWeight: 700, color: 'var(--sf-ink)' }}>
                {chip.priceStr}
              </span>
              <span className="num" style={{ fontSize: 11, color: 'var(--sf-muted)' }}>{chip.deltaStr}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Cards */}
      <div className="sf-cards">
        {vms.map((vm) => (
          <FlavorCard
            key={vm.id}
            vm={vm}
            onOpenModal={() => setModalTier(vm.id)}
            onCycleMode={cycleMode}
            onStepFreq={stepFreq}
            onStepDur={stepDur}
          />
        ))}
      </div>

      {/* Rates summary + open-settings strip */}
      <div className="sf-rates-strip" style={{ marginTop: 14 }}>
        <RatesStrip services={services} settings={settings} onSettingsClick={() => setSettingsOpen(true)} />
      </div>
        </div>
        {/* /sf-flavors-col */}
      </div>
      {/* /sf-estimate-layout */}

      {/* Estimate settings modal */}
      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={settings}
        onSettingsChange={onSettingsChange}
      />

      {/* Deeper-tweaks modal */}
      {modalFlavor && modalPriced ? (
        <DeeperTweaksModal
          flavor={modalFlavor}
          startupStr={money(modalPriced.startupPrice)}
          startupCatalog={STARTUP_CATALOG}
          onClose={() => setModalTier(null)}
          onSetDays={(n) => setDays(modalTier, n)}
          onStepBlow={(d) => stepBlow(modalTier, d)}
          onSetPrune={(v) => setPrune(modalTier, v)}
          onSetCov={(i) => setCov(modalTier, i)}
          onToggleStartup={(k) => toggleStartup(modalTier, k)}
          onStepStartup={(k, d) => stepStartup(modalTier, k, d)}
        />
      ) : null}
    </div>
  );
}
